import { useTranslation } from "react-i18next";
import { Link, useInRouterContext, useNavigate } from "react-router-dom";
import { normalizeErrorStateVariant } from "./errorStateUtils";
import "./PmErrorState.css";

const VARIANT_DEFAULTS = {
  not_found: {
    code: "404",
    titleKey: "common.error_state.not_found.title",
    descriptionKey: "common.error_state.not_found.description",
    primary: "dashboard",
  },
  forbidden: {
    code: "403",
    titleKey: "common.error_state.forbidden.title",
    descriptionKey: "common.error_state.forbidden.description",
    primary: "dashboard",
  },
  load_error: {
    code: "",
    titleKey: "common.error_state.load_error.title",
    descriptionKey: "common.error_state.load_error.description",
    primary: "retry",
  },
  empty: {
    code: "",
    titleKey: "common.error_state.empty.title",
    descriptionKey: "common.error_state.empty.description",
    primary: null,
  },
};

function GeometricMark({ variant }) {
  const stroke = "currentColor";
  return (
    <svg
      className="pm-error-state__mark pls-anim"
      viewBox="0 0 148 148"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="74" cy="74" r="58" stroke={stroke} strokeWidth="1.25" opacity="0.18" />
      <circle cx="74" cy="74" r="44" stroke={stroke} strokeWidth="1.5" opacity="0.28" strokeDasharray={variant === "empty" ? "4 7" : undefined} />
      {variant === "not_found" && (
        <>
          <path
            d="M46 92c8-18 20-28 28-28s20 10 28 28"
            stroke={stroke}
            strokeWidth="2.2"
            strokeLinecap="round"
            opacity="0.55"
          />
          <circle cx="60" cy="62" r="3.5" fill={stroke} opacity="0.45" />
          <circle cx="88" cy="62" r="3.5" fill={stroke} opacity="0.45" />
        </>
      )}
      {variant === "forbidden" && (
        <>
          <path
            d="M74 42l26 10v18c0 16.5-11 30.5-26 36-15-5.5-26-19.5-26-36V52l26-10z"
            stroke={stroke}
            strokeWidth="2.2"
            opacity="0.65"
          />
          <path
            d="M66 76l6 6 12-14"
            stroke={stroke}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
          />
        </>
      )}
      {variant === "load_error" && (
        <>
          <path
            d="M50 74a24 24 0 0138-19"
            stroke={stroke}
            strokeWidth="2.4"
            strokeLinecap="round"
            opacity="0.7"
          />
          <path
            d="M98 74a24 24 0 01-38 19"
            stroke={stroke}
            strokeWidth="2.4"
            strokeLinecap="round"
            opacity="0.35"
          />
          <circle cx="88" cy="55" r="3" fill={stroke} opacity="0.7" />
        </>
      )}
      {variant === "empty" && (
        <rect
          x="50"
          y="58"
          width="48"
          height="34"
          rx="8"
          stroke={stroke}
          strokeWidth="2"
          opacity="0.45"
        />
      )}
    </svg>
  );
}

function ActionButton({ kind, children, onClick, to, inRouter }) {
  const className = `pm-error-state__btn pm-error-state__btn--${kind}`;
  if (to) {
    if (inRouter) {
      return (
        <Link className={className} to={to}>
          {children}
        </Link>
      );
    }
    return (
      <a className={className} href={to}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {children}
    </button>
  );
}

function goTo(path, navigate) {
  if (navigate) {
    navigate(path);
    return;
  }
  window.location.assign(path);
}

export function PmErrorState(props) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return <PmErrorStateRouted {...props} />;
  }
  return <PmErrorStateInner {...props} inRouter={false} navigate={null} />;
}

function PmErrorStateRouted(props) {
  const navigate = useNavigate();
  return <PmErrorStateInner {...props} inRouter navigate={navigate} />;
}

function PmErrorStateInner(props) {
  const {
    variant = "load_error",
    layout = "module",
    title,
    description,
    details,
    onRetry,
    retryLabel,
    onBack,
    backTo,
    backLabel,
    dashboardTo = "/dashboard",
    dashboardLabel,
    primaryTo,
    primaryLabel,
    hideActions = false,
    className = "",
    inRouter = false,
    navigate = null,
  } = props;
  const { t } = useTranslation();
  const resolved = normalizeErrorStateVariant(variant);
  const meta = VARIANT_DEFAULTS[resolved];
  const isInDashboard = typeof window !== "undefined" && window.location.pathname.startsWith("/dashboard");
  const homeTo = isInDashboard ? dashboardTo : "/";
  const role = resolved === "empty" ? "status" : "alert";
  const detailList = details
    ? (Array.isArray(details) ? details : [details]).filter(Boolean)
    : [];

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      goTo(backTo, navigate);
      return;
    }
    if (window.history.length > 1) {
      if (navigate) {
        navigate(-1);
        return;
      }
      window.history.back();
      return;
    }
    goTo(homeTo, navigate);
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  const showRetry = resolved === "load_error" || Boolean(onRetry);
  const resolvedPrimaryTo = primaryTo || (meta.primary === "dashboard" ? homeTo : null);
  const resolvedPrimaryLabel =
    primaryLabel ||
    (meta.primary === "dashboard"
      ? dashboardLabel || (isInDashboard ? t("common.error_state.action.dashboard") : t("common.error_state.action.home"))
      : null);

  return (
    <div
      className={`pm-error-state pm-error-state--${resolved} pm-error-state--${layout} ${className}`.trim()}
      role={role}
      aria-live="polite"
    >
      <div className="pm-error-state__card pls-anim">
        <div className="pm-error-state__art">
          <div className="pm-error-state__glow pls-anim" />
          <GeometricMark variant={resolved} />
          {meta.code ? <span className="pm-error-state__code">{meta.code}</span> : null}
        </div>
        <h1 className="pm-error-state__title">{title || t(meta.titleKey)}</h1>
        <p className="pm-error-state__description">
          {description || t(meta.descriptionKey)}
        </p>
        {detailList.length > 0 && (
          <div className="pm-error-state__details">
            <p className="pm-error-state__details-label">
              {t("common.error_state.details_heading")}
            </p>
            <ul>
              {detailList.map((item, index) => (
                <li key={`${index}-${String(item)}`}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {!hideActions && (
          <div className="pm-error-state__actions">
            {showRetry && meta.primary === "retry" && (
              <ActionButton kind="primary" onClick={handleRetry} inRouter={inRouter}>
                {retryLabel || t("common.error_state.action.retry")}
              </ActionButton>
            )}
            {resolvedPrimaryTo && (
              <ActionButton
                kind={showRetry && meta.primary === "retry" ? "secondary" : "primary"}
                to={resolvedPrimaryTo}
                inRouter={inRouter}
              >
                {resolvedPrimaryLabel}
              </ActionButton>
            )}
            <ActionButton kind="secondary" onClick={handleBack} inRouter={inRouter}>
              {backLabel || t("common.error_state.action.back")}
            </ActionButton>
            {showRetry && meta.primary !== "retry" && (
              <ActionButton kind="secondary" onClick={handleRetry} inRouter={inRouter}>
                {retryLabel || t("common.error_state.action.retry")}
              </ActionButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PmErrorState;
